<template>
  <app-page-gradient-background>
    <app-page-wrapper class="!px-0 pb-32 md:!px-8">
      <div class="hidden items-center justify-between pb-4 md:flex">
        <course-page-breadcrumb
          v-if="!isRegistrationLink && !isProjectPreview"
          class="text-primary-950"
          :course="course"
        />

        <course-visibility-chip
          v-if="isAdmin"
          admin-page-link
          :course="course"
          size="small"
        />
      </div>

      <course-page-grid>
        <div>
          <course-page-content-wrapper class="py-6 md:py-11">
            <course-page-header :course="course" :next-session="nextSession" />
          </course-page-content-wrapper>

          <app-card class="!rounded-none md:!rounded-lg">
            <div class="px-6 md:px-0">
              <course-page-cover class="rounded-lg" :course="course" />
            </div>

            <div
              class="sticky top-[--layout-offset-top] z-10 mt-4 hidden !rounded-none bg-white/90 backdrop-blur transition-all duration-navigation md:!rounded-lg xl:block"
            >
              <course-page-content-wrapper>
                <course-page-navigation
                  class="py-6"
                  :course="course"
                  :packages="packages"
                />
              </course-page-content-wrapper>
            </div>

            <course-page-content-wrapper class="grid gap-sections py-16">
              <course-page-description :course="course" />

              <course-page-audience
                v-if="course.audience.length > 0"
                :course="course"
              />

              <course-page-outcomes
                v-if="course.outcomes.length > 0"
                :course="course"
              />

              <course-page-teacher-details :teacher="course.teacher" />

              <course-page-ratings
                v-if="ratings"
                :course="course"
                :ratings="ratings"
              />

              <course-page-program
                :course="course"
                :next-session="nextSession"
              />

              <course-page-packages
                v-if="packages.length > 0"
                :course="course"
                :next-session="nextSession"
                :packages="packages"
              />

              <why-pollen-banner
                v-if="!course.is_masterclass"
                class="rounded-lg bg-deco2 p-8 lg:p-16"
              />

              <course-page-support
                v-if="!isRegistrationLink"
                :course="course"
                :session="nextSession"
              />
            </course-page-content-wrapper>
          </app-card>
        </div>

        <aside class="hidden pt-10 lg:block">
          <div class="sticky-below-header grid items-start gap-6">
            <course-page-enrolment
              :course="course"
              data-cy-next-session-enrolment
              :packages="packages"
              :session="nextSession"
            />

            <course-page-other-session-list
              v-if="otherSessions.length > 0"
              :course="course"
              :sessions="otherSessions"
            />
          </div>
        </aside>
      </course-page-grid>

      <course-page-other-courses-list
        v-if="!isRegistrationLink"
        class="px-6 pt-32 md:px-0"
        :course="course"
      />
    </app-page-wrapper>

    <course-page-mobile-bottom-sheet
      class="lg:hidden"
      :course="course"
      :next-session="nextSession"
    />

    <course-page-seo-content :course="course" />
  </app-page-gradient-background>
</template>

<script lang="ts" setup>
import { useCoursePackages } from "~/business-areas/course-package/composables/course-packages.hook";
import CoursePageAudience from "~/pages/courses/[slug]/_includes/components/course-page-audience.vue";
import CoursePageBreadcrumb from "~/pages/courses/[slug]/_includes/components/course-page-breadcrumb.vue";
import CoursePageContentWrapper from "~/pages/courses/[slug]/_includes/components/course-page-content-wrapper.vue";
import CoursePageCover from "~/pages/courses/[slug]/_includes/components/course-page-cover.vue";
import CoursePageDescription from "~/pages/courses/[slug]/_includes/components/course-page-description.vue";
import CoursePageGrid from "~/pages/courses/[slug]/_includes/components/course-page-grid.vue";
import CoursePageHeader from "~/pages/courses/[slug]/_includes/components/course-page-header.vue";
import CoursePageMobileBottomSheet from "~/pages/courses/[slug]/_includes/components/course-page-mobile-bottom-sheet.vue";
import CoursePageNavigation from "~/pages/courses/[slug]/_includes/components/course-page-navigation.vue";
import CoursePageOtherCoursesList from "~/pages/courses/[slug]/_includes/components/course-page-other-courses-list.vue";
import CoursePageOtherSessionList from "~/pages/courses/[slug]/_includes/components/course-page-other-session-list.vue";
import CoursePageOutcomes from "~/pages/courses/[slug]/_includes/components/course-page-outcomes.vue";
import CoursePagePackages from "~/pages/courses/[slug]/_includes/components/course-page-packages.vue";
import CoursePageRatings from "~/pages/courses/[slug]/_includes/components/course-page-ratings.vue";
import CoursePageSeoContent from "~/pages/courses/[slug]/_includes/components/course-page-seo-content.vue";
import CoursePageSupport from "~/pages/courses/[slug]/_includes/components/course-page-support.vue";
import CoursePageTeacherDetails from "~/pages/courses/[slug]/_includes/components/course-page-teacher-details.vue";
import CoursePageEnrolment from "~/pages/courses/[slug]/_includes/components/enrolment/course-page-enrolment.vue";
import CoursePageProgram from "~/pages/courses/[slug]/_includes/components/program/course-page-program.vue";
import { useCoursePageData } from "~/pages/courses/[slug]/_includes/composables/course-page.data";
import { useCoursePageMeta } from "~/pages/courses/[slug]/_includes/composables/course-page-meta.hook";
import type { AnalyticsCourseView } from "~~/model/analytics.model";

const route = useRoute("courses-slug");
const slug = route.params.slug as string;

const { isAdmin } = useCurrentUser();

const {
  course,
  otherSessions,
  nextSession,
  isRegistrationLink,
  isProjectPreview,
} = await useCoursePageData(slug, {
  registration_token: route.query.registration_token as string,
  from_project: !!route.query.from_project,
});

const { packages } = useCoursePackages({
  course: course.value!,
  course_session_id: nextSession.value?.id,
});

const { data: ratings } = useLazyFetch(
  `/api/courses/${course.value!.id}/ratings`,
);

useCoursePageMeta({
  course,
  nextSession,
  packages,
  ratingsWithComment: computed(
    () => ratings.value?.list.filter((rating) => !!rating.comment) ?? [],
  ),
});

const analyticsContext: { course?: AnalyticsCourseView } = {
  course: course.value
    ? {
        id: course.value?.id,
        slug: course.value?.slug,
        tags: course.value?.tags.map((tag) => tag.slug),
        main_tag: course.value?.tags[0]?.slug ?? "",
      }
    : undefined,
};

defineAnalyticsContext(analyticsContext);
</script>
